/*!
 * law-core.js — the verdict layer of /signage-law-check («Проверка вывески и
 * сайта по закону о русском языке»).
 *
 * Pure: no DOM, no fetch, no copy. The page (law-ui.js) loads the data and maps
 * every code below to a sentence; the node tests (tests/js/signage-law-*.test.mjs)
 * drive the same functions. Shared between browser (window.SignageLaw) and node
 * (module.exports).
 *
 * What it decides, element by element (a line of a sign, a button of a site):
 *
 *  • Latin text → a run of foreign words. A run is exempt when the person marked
 *    it as a registered trademark or a company name (п. 4 ст. 10.1 ЗоЗПП,
 *    ч. 3 ст. 3 53-ФЗ), is a well-known brand, an abbreviation, an address or a
 *    code. Otherwise it needs an equal Russian duplicate (ч. 2 ст. 3 53-ФЗ:
 *    «идентичными по содержанию, равнозначными по размещению и техническому
 *    оформлению (иметь одинаковые параметры — цвет, тип и размер шрифта)»).
 *    The duplicate is looked for on the same carrier — inline on the same line,
 *    or another line that translates the run (our English glossary).
 *
 *  • Cyrillic word → the state of the word in OUR lexicon (public/data/
 *    signage-law/lexicon.json, every page reference checked against the PDF of
 *    the normative dictionary): ok (in the foreign-words dictionary — no common
 *    analogue), orfo (orthographic dictionary only, no substitute given), alt
 *    (the orthographic dictionary itself offers a Russian substitute), none (in
 *    neither, or only in another sense). A misspelling of a dictionary word is
 *    its own finding («кэшбэк» — словарь пишет «кешбэк»). A word our lexicon
 *    does not know but the anglicism engine flags is `unknown` — we say we did
 *    not check it and link to the dictionaries, never pretend.
 */
(function (root, factory) {
    'use strict';
    const api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (typeof window !== 'undefined') window.SignageLaw = api;
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    const VERSION = '1.0.0';

    /* ─────────── carriers and the regime each one is under ───────────
     * `law` is a code the page turns into the paragraph that names the norm;
     * `site` switches on the language-version logic (a whole /en/ page is a
     * foreign-language version, not a missing duplicate). */
    const OBJECTS = {
        sign:  { law: 'zozpp101' },
        site:  { law: 'zozpp101', site: true },
        info:  { law: 'zozpp8' },
        menu:  { law: 'menu1515' },
        label: { law: 'techreg' },
        ad:    { law: 'ad38' },
    };

    const SEV = { ok: 0, note: 1, warn: 2, fail: 3 };

    /* ─────────── text helpers ─────────── */
    function norm(s) {
        return String(s == null ? '' : s).toLowerCase().replace(/ё/g, 'е').replace(/[’`]/g, "'").trim();
    }
    const CYR = /[а-яё]/i;
    const LAT = /[a-z]/i;
    const DIGIT = /\p{N}/u;

    // Addresses are not words: domain names are outside the new rules (разъяснение
    // Управления Роспотребнадзора по Республике Алтай, 08.07.2026), e-mail and URL
    // are technical identifiers. Masked before tokenising, reported separately.
    const ADDR_RES = [
        /\b(?:https?:\/\/|www\.)[^\s<>"'«»]+/gi,
        /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g,
        // a domain ends in «not a letter», not \b: \b knows only Latin letters, so «.рф» never matched
        /(?:^|[\s(«"])((?:[a-z0-9-]+\.)+(?:ru|su|com|net|org|io|app|online|shop|store|site|info|me|pro|moscow|spb|biz|club|tech|рф))(?![\p{L}\p{N}_])/giu,
        /(?:^|[\s(«"])((?:[а-яё0-9-]+\.)+рф)(?![\p{L}\p{N}_])/giu,
        /(?:^|\s)[@#][\p{L}\p{N}_]{2,}/gu,
    ];

    function maskAddresses(text) {
        const found = [];
        let masked = String(text || '');
        for (const re of ADDR_RES) {
            re.lastIndex = 0;
            masked = masked.replace(re, function () {
                const args = Array.prototype.slice.call(arguments);
                const m = args[0];
                let off = 0;
                for (let k = args.length - 1; k > 0; k--) { if (typeof args[k] === 'number') { off = args[k]; break; } }
                const g1 = typeof args[1] === 'string' ? args[1] : null;
                const val = g1 != null ? g1 : m;
                const lead = g1 != null ? m.lastIndexOf(g1) : 0;
                const s = off + lead;
                const lt = val.length - val.trimStart().length;
                found.push({ text: val.trim(), start: s + lt, end: s + val.length });
                return m.slice(0, lead) + ' '.repeat(val.length);
            });
        }
        return { masked, addresses: found };
    }

    const WORD_RE = /[\p{L}\p{M}\p{N}]+(?:['’][\p{L}\p{M}]+|-[\p{L}\p{M}\p{N}]+)*/gu;

    const ROMAN = /^(?=[MDCLXVI])M{0,4}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/;

    function scriptOf(tok) {
        if (ROMAN.test(tok)) return 'code';
        const c = CYR.test(tok), l = LAT.test(tok);
        if (c && l) return 'mix';
        if (c) return 'cyr';
        if (l) return 'lat';
        return 'other';
    }

    function tokenize(text) {
        const { masked, addresses } = maskAddresses(text);
        const out = [];
        WORD_RE.lastIndex = 0;
        let m;
        while ((m = WORD_RE.exec(masked)) !== null) {
            const t = m[0];
            const script = DIGIT.test(t) ? 'code' : scriptOf(t);
            out.push({ t, start: m.index, end: m.index + t.length, script });
        }
        return { tokens: out, addresses };
    }

    /* ─────────── Russian inflection (enough to find a loanword's forms) ─────────── */
    const END_CONS = new Set(['а', 'у', 'ом', 'е', 'ы', 'и', 'ов', 'ев', 'ам', 'ям', 'ами', 'ями', 'ах', 'ях', 'ем', 'ой', 'ой', 'ей']);
    const END_VOWEL = new Set(['ы', 'и', 'е', 'у', 'ю', 'ой', 'ей', 'ам', 'ям', 'ами', 'ями', 'ах', 'ях', 'я', 'а', 'ом', 'ем']);
    // Short words take only the endings that cannot collide with a native word.
    const END_SHORT = new Set(['а', 'у', 'ом', 'ы', 'ов', 'ам', 'ами', 'ах']);

    function formMatches(key, tok, exact) {
        if (tok === key) return true;
        if (exact) {
            if (key.length < 3 || !tok.startsWith(key)) return false;
            return END_SHORT.has(tok.slice(key.length)) && !/[аеиоуыэюяь]$/.test(key);
        }
        if (/[аеиоуыэюя]$/.test(key) && !/[ая]$/.test(key)) return false;   // кафе, латте, тату — несклоняемые
        if (/[ая]$/.test(key)) {
            const base = key.slice(0, -1);
            return tok.startsWith(base) && END_VOWEL.has(tok.slice(base.length));
        }
        if (/[ьй]$/.test(key)) {
            const base = key.slice(0, -1);
            return tok.startsWith(base) && (END_VOWEL.has(tok.slice(base.length)) || tok.slice(base.length) === 'ю');
        }
        return tok.startsWith(key) && END_CONS.has(tok.slice(key.length));
    }

    /* ─────────── lexicon ─────────── */
    function compileLexicon(json) {
        const byKey = new Map();
        const byHead = new Map();
        const entries = (json && json.entries) || [];
        for (const e of entries) {
            const k = norm(e.w);
            byKey.set(k, e);
            const head = k.slice(0, 3);
            if (!byHead.has(head)) byHead.set(head, []);
            byHead.get(head).push({ k, e });
        }
        for (const arr of byHead.values()) arr.sort((a, b) => b.k.length - a.k.length);
        return { meta: (json && json.meta) || {}, byKey, byHead, size: entries.length, words: entries.filter(e => !e.spell).length };
    }

    /** → { entry, spellOf? } | null. A spelling entry resolves to the dictionary word it misspells. */
    function lookupWord(lex, surface) {
        if (!lex) return null;
        const tok = norm(surface);
        let hit = lex.byKey.get(tok) || null;
        if (!hit) {
            const cands = lex.byHead.get(tok.slice(0, 3)) || [];
            for (const c of cands) { if (formMatches(c.k, tok, !!c.e.exact)) { hit = c.e; break; } }
        }
        if (!hit) return null;
        if (hit.spell) {
            const target = lex.byKey.get(norm(hit.spell));
            return { entry: target || { w: hit.spell, st: 'ok' }, spellOf: hit.w, spell: hit.spell };
        }
        return { entry: hit };
    }

    /* ─────────── English glossary (our own; for duplicates) ─────────── */
    function compileGlossary(json, extra) {
        const phrases = new Map();
        const words = new Map();
        const j = json || {};
        for (const k in (j.phrases || {})) phrases.set(norm(k), j.phrases[k]);
        for (const k in (j.words || {})) words.set(norm(k), j.words[k]);
        if (extra) for (const [k, v] of extra) if (!words.has(k) && v && v.length) words.set(k, v);
        const brands = new Set((j.brands || []).map(norm));
        const abbr = new Set((j.abbr || []).map(s => String(s).toUpperCase()));
        const abbrRu = j.abbr_ru || {};
        let maxPhrase = 1;
        for (const k of phrases.keys()) maxPhrase = Math.max(maxPhrase, k.split(/\s+/).length);
        return { phrases, words, brands, abbr, abbrRu, maxPhrase };
    }

    function glossWord(g, w) {
        const k = norm(w);
        if (g.words.has(k)) return g.words.get(k);
        // plural / possessive tolerance: shoes→shoe? no — only the safe 's
        const k2 = k.replace(/'s$/, '');
        if (k2 !== k && g.words.has(k2)) return g.words.get(k2);
        if (/s$/.test(k) && g.words.has(k.slice(0, -1))) return g.words.get(k.slice(0, -1));
        return null;
    }

    /**
     * Gloss a run of Latin words: longest phrase first, then word by word.
     * → { parts: [{en, ru: [...] | null, phrase: bool}], suggest: string, known, total }
     */
    function glossRun(g, words) {
        const parts = [];
        const lw = words.map(norm);
        let i = 0, known = 0, total = 0;
        while (i < lw.length) {
            let took = false;
            for (let n = Math.min(g.maxPhrase, lw.length - i); n >= 2; n--) {
                const k = lw.slice(i, i + n).join(' ');
                if (g.phrases.has(k)) {
                    parts.push({ en: words.slice(i, i + n).join(' '), ru: g.phrases.get(k), phrase: true });
                    known += n; total += n; i += n; took = true; break;
                }
            }
            if (took) continue;
            const one = lw[i];
            const ph = g.phrases.get(one);
            const ru = ph || glossWord(g, one);
            const isStop = Array.isArray(ru) && ru.length === 0;
            if (!isStop) total++;
            if (ru && ru.length) known++;
            parts.push({ en: words[i], ru: ru, phrase: false, stop: isStop });
            i++;
        }
        const suggest = known > 0 ? parts.filter(p => p.ru && p.ru.length).map(p => p.ru[0]).join(' ') : '';
        return { parts, suggest, known, total };
    }

    /* ─────────── Latin runs ─────────── */
    const RUN_GAP = /^[\s&.,:;!?'’\-–—/+|·•*"«»()]*$/;

    function latinRuns(text, tokens) {
        const runs = [];
        let cur = null;
        for (const tk of tokens) {
            if (tk.script === 'lat') {
                if (cur && RUN_GAP.test(text.slice(cur.end, tk.start))) {
                    cur.end = tk.end; cur.words.push(tk.t);
                } else {
                    if (cur) runs.push(cur);
                    cur = { start: tk.start, end: tk.end, words: [tk.t] };
                }
            } else if (tk.script !== 'other') {
                if (cur) { runs.push(cur); cur = null; }
            }
        }
        if (cur) runs.push(cur);
        for (const r of runs) {
            r.text = text.slice(r.start, r.end);
            const before = text.slice(Math.max(0, r.start - 2), r.start), after = text.slice(r.end, r.end + 2);
            r.quoted = /[“«"„]\s*$/.test(before) && /^\s*[.,!?:;]?\s*[”»"“]/.test(after) && CYR.test(text);
        }
        return runs;
    }

    function isAbbr(g, w) {
        const W = String(w).toUpperCase();
        return g.abbr.has(W) && (w === W || W.indexOf('-') !== -1);
    }

    function runKind(run, g, marks, extraBrands) {
        const key = norm(run.text).replace(/\s+/g, ' ');
        if (marks) {
            for (const m of marks) {
                const mk = norm(m.text).replace(/\s+/g, ' ');
                if (mk && (key === mk || key.indexOf(mk) !== -1 || mk.indexOf(key) !== -1)) {
                    return { kind: m.kind === 'firm' ? 'firm' : m.kind === 'name' ? 'name' : 'tm', mark: m };
                }
            }
        }
        const brandSet = g ? g.brands : new Set();
        if (brandSet.has(key) || (extraBrands && extraBrands.has(key))) return { kind: 'brand' };
        const lw = run.words.map(norm);
        if (lw.every(w => brandSet.has(w) || (extraBrands && extraBrands.has(w)))) return { kind: 'brand' };
        if (g && run.words.every(w => isAbbr(g, w))) return { kind: 'abbr' };
        // A foreign title or quotation inside quotation marks, three words and up,
        // inside Russian text («доклад “Competing grammatical …”»): usually the name
        // of someone else's work — a warning for the person to decide, not a fail.
        if (run.quoted && run.words.length >= 3) return { kind: 'quote' };
        return { kind: 'foreign' };
    }

    /* ─────────── stems for duplicate matching ─────────── */
    function stemLite(w) {
        const k = norm(w).replace(/[^а-яa-z0-9-]/g, '');
        if (k.length <= 4) return k;
        return k.replace(/(ами|ями|ого|ему|ому|ыми|ими|ах|ях|ам|ям|ой|ей|ом|ем|ую|юю|ая|яя|ое|ее|ые|ие|ый|ий|ов|ев|а|я|у|ю|ы|и|е|о|ь)$/, '');
    }
    function cyrStems(text) {
        const s = new Set();
        const m = String(text || '').match(/[а-яё]+(?:-[а-яё]+)*/gi) || [];
        for (const w of m) {
            s.add(stemLite(w));
            for (const p of w.split('-')) if (p.length > 2) s.add(stemLite(p));
        }
        return s;
    }
    function candidateCovered(ru, stems) {
        const words = String(ru).match(/[а-яё]+/gi) || [];
        const content = words.filter(w => w.length > 2);
        if (!content.length) return false;
        let hits = 0;
        for (const w of content) {
            const st = stemLite(w);
            let ok = stems.has(st);
            if (!ok && st.length >= 5) { for (const x of stems) { if (x.length >= 5 && (x.startsWith(st.slice(0, 5)) || st.startsWith(x.slice(0, 5)))) { ok = true; break; } } }
            if (ok) hits++;
        }
        return hits >= Math.ceil(content.length / 2);
    }

    /** How much of a foreign run the Russian text translates (0..1, null when nothing is known). */
    function coverage(gloss, ruText) {
        const d = coverageDetail(gloss, ruText);
        return d ? d.cov : null;
    }

    /**
     * cov  — share of the run's known words that the Russian text translates;
     * prec — share of the Russian text's own words that those translations
     *        account for (a short «Кофейня» beats «Кофейня «Бьюти Хаус»» as the
     *        duplicate of «Coffee house»: both cover it, only one IS it).
     */
    function coverageDetail(gloss, ruText) {
        const stems = cyrStems(ruText);
        const used = new Set();
        let total = 0, covered = 0;
        for (const p of gloss.parts) {
            if (p.stop) continue;
            const n = p.phrase ? p.en.split(/\s+/).length : 1;
            if (!p.ru || !p.ru.length) continue;
            total += n;
            let hit = false;
            for (const r of p.ru) {
                if (candidateCovered(r, stems)) {
                    hit = true;
                    for (const w of (String(r).match(/[а-яё]+/gi) || [])) if (w.length > 2) used.add(stemLite(w));
                }
            }
            if (hit) covered += n;
        }
        if (!total) return null;
        const own = Array.from(stems).filter(x => x.length > 2);
        let explained = 0;
        for (const x of own) {
            for (const u of used) { if (x === u || (x.length >= 5 && u.length >= 5 && (x.startsWith(u.slice(0, 5)) || u.startsWith(x.slice(0, 5))))) { explained++; break; } }
        }
        return { cov: covered / total, prec: own.length ? explained / own.length : 0 };
    }

    /* Сокращения адреса и реквизитов — с точкой: русские слова, а не заимствования. */
    const ADDR_ABBR = new Set(['корп', 'стр', 'пом', 'эт', 'оф', 'кв', 'вл', 'влд', 'мкр', 'пр', 'просп', 'пер', 'ш', 'д', 'ул', 'пл', 'наб', 'обл', 'г', 'пос', 'тел', 'каб', 'лит', 'соор', 'зд']);

    /* ─────────── analysis of one element ─────────── */
    function issue(code, sev, extra) { return Object.assign({ code, sev }, extra || {}); }

    /**
     * text  — one carrier element (a line of a sign, a button, an alt text)
     * ctx   — { lex, gloss, marks: [{text, kind:'tm'|'firm', no?}], angl: {dict, analyze} | null,
     *           extraBrands: Set }
     */
    function analyzeElement(text, ctx) {
        ctx = ctx || {};
        const raw = String(text == null ? '' : text);
        const { tokens, addresses } = tokenize(raw);
        const runs = latinRuns(raw, tokens);
        const issues = [];
        const words = [];

        for (const r of runs) {
            const k = runKind(r, ctx.gloss, ctx.marks, ctx.extraBrands);
            r.kind = k.kind;
            if (k.mark) r.mark = k.mark;
            r.gloss = ctx.gloss ? glossRun(ctx.gloss, r.words) : { parts: [], suggest: '', known: 0, total: r.words.length };
            if (r.kind === 'abbr' && ctx.gloss) {
                const ru = r.words.map(w => ctx.gloss.abbrRu[String(w).toUpperCase()]).filter(Boolean);
                if (ru.length) r.abbrRu = ru;
            }
        }

        // anglicism engine hits (only for Cyrillic words our lexicon does not know)
        let engineHits = [];
        if (ctx.angl && ctx.angl.dict && typeof ctx.angl.analyze === 'function' && CYR.test(raw)) {
            try { engineHits = (ctx.angl.analyze(raw, ctx.angl.dict, { strictness: 'balanced' }).hits || []).filter(h => !h.raw); }
            catch (_) { engineHits = []; }
        }
        const hitAt = (s, e) => engineHits.find(h => h.start < e && h.end > s) || null;

        for (const tk of tokens) {
            if (tk.script === 'mix') {
                issues.push(issue('mixed_script', 'warn', { word: tk.t, start: tk.start, end: tk.end }));
                continue;
            }
            if (tk.script !== 'cyr') continue;
            // An address abbreviation with its dot («корп. 2», «стр. 6», «ш.») is a
            // Russian word cut short, not a borrowing: the engine read «корп» as
            // «corp» on every address of a cafe chain (coffeemania.ru, QA 23.10).
            if (raw.charAt(tk.end) === '.' && ADDR_ABBR.has(tk.t.toLowerCase())) continue;
            const w = { surface: tk.t, start: tk.start, end: tk.end, state: null };
            let look = lookupWord(ctx.lex, tk.t);
            let viaParts = null;
            if (!look && tk.t.indexOf('-') > 0) {
                const parts = tk.t.split('-').filter(p => p.length > 1);
                const res = parts.map(p => ({ p, look: lookupWord(ctx.lex, p) }));
                if (res.some(x => x.look)) viaParts = res;
            }
            if (look) {
                const e = look.entry;
                w.state = e.st || 'ok';
                w.entry = e;
                if (look.spellOf) { w.spell = look.spell; w.spellOf = look.spellOf; }
            } else if (viaParts) {
                // A compound of dictionary parts and ordinary Russian ones («онлайн-формате»)
                // is normal word formation — the orthographic dictionary even lists such
                // first parts («онлайн-…»). Only a part that is itself a problem speaks.
                let off = 0;
                const parts = tk.t.split('-').map(p => { const r = { p, s: tk.start + off, e: tk.start + off + p.length }; off += p.length + 1; return r; });
                let worst = null;
                const RANK = { ok: 0, orfo: 1, unknown: 2, alt: 3, none: 4 };
                for (const part of parts) {
                    const lk = part.p.length > 1 ? lookupWord(ctx.lex, part.p) : null;
                    let st = lk ? (lk.entry.st || 'ok') : null;
                    if (!lk) { const h = hitAt(part.s, part.e); if (h && h.tier !== 'needed') st = 'unknown'; }
                    part.st = st; part.entry = lk ? lk.entry : null;
                    if (st && (!worst || RANK[st] > RANK[worst.st])) worst = part;
                }
                if (!worst || worst.st === 'ok' || worst.st === 'orfo') continue;
                w.state = worst.st === 'unknown' ? 'parts' : worst.st;
                w.parts = parts.map(x => ({ p: x.p, st: x.st, entry: x.entry }));
                if (worst.entry) w.entry = worst.entry;
            } else {
                const h = hitAt(tk.start, tk.end);
                if (!h) continue;                       // a Russian word — nothing to say
                w.state = 'unknown';
                w.tier = h.tier;
                w.nat = !!(h.entry && h.entry.nat);
                w.engineAlt = (h.entry && (h.entry._altSafe || h.entry.alt)) || [];
                w.lemma = h.entry && h.entry.w;
            }
            words.push(w);
        }

        for (const w of words) {
            const st = w.state;
            // A misspelling of a dictionary word is ONE finding: «пишется «кешбэк»» with
            // that word's page. A replacement or a missing word stays its own line.
            if (w.spell) {
                issues.push(issue('spell', 'warn', { word: w.surface, spell: w.spell, entry: w.entry, state: st, start: w.start, end: w.end }));
                if (st === 'ok' || st === 'orfo') continue;
            }
            if (st === 'none') issues.push(issue(w.entry && w.entry.sense ? 'word_sense' : 'word_none', 'fail', { word: w.surface, entry: w.entry, start: w.start, end: w.end }));
            else if (st === 'alt') issues.push(issue('word_alt', 'warn', { word: w.surface, entry: w.entry, start: w.start, end: w.end }));
            else if (st === 'orfo') issues.push(issue('word_orfo', 'note', { word: w.surface, entry: w.entry, start: w.start, end: w.end }));
            else if (st === 'parts') issues.push(issue('word_parts', 'note', { word: w.surface, parts: w.parts, start: w.start, end: w.end }));
            // A borrowing the engine itself calls naturalised («терминал», «офис») is in the
            // dictionaries as an ordinary word — a note «not checked by us», not a yellow line
            // on every address of a page.
            else if (st === 'unknown') issues.push(issue('word_unknown', w.tier === 'needed' || w.nat ? 'note' : 'warn', { word: w.surface, alts: w.engineAlt, lemma: w.lemma, start: w.start, end: w.end }));
            else if (st === 'ok' && w.entry && w.entry.razg) issues.push(issue('word_razg', 'note', { word: w.surface, entry: w.entry, start: w.start, end: w.end }));
            else if (st === 'ok') issues.push(issue('word_ok', 'ok', { word: w.surface, entry: w.entry, start: w.start, end: w.end }));
        }

        for (const r of runs) {
            if (r.kind === 'foreign') issues.push(issue('latin', 'fail', { run: r, start: r.start, end: r.end }));
            else if (r.kind === 'tm' || r.kind === 'firm') issues.push(issue(r.kind, 'ok', { run: r, start: r.start, end: r.end }));
            else if (r.kind === 'name') issues.push(issue('name', 'note', { run: r, start: r.start, end: r.end }));
            else if (r.kind === 'quote') issues.push(issue('quote', 'warn', { run: r, start: r.start, end: r.end }));
            else if (r.kind === 'brand') issues.push(issue('brand', 'note', { run: r, start: r.start, end: r.end }));
            else if (r.kind === 'abbr') issues.push(issue('abbr', 'note', { run: r, start: r.start, end: r.end }));
        }
        for (const a of addresses) issues.push(issue('address', 'ok', { text: a.text, start: a.start, end: a.end }));

        const hasCyr = tokens.some(t => t.script === 'cyr');
        return {
            text: raw,
            runs, words, issues, addresses,
            hasCyr,
            foreign: runs.filter(r => r.kind === 'foreign'),
            status: statusOf(issues),
        };
    }

    function statusOf(issues) {
        let s = 'ok';
        for (const i of issues) if (SEV[i.sev] > SEV[s]) s = i.sev;
        return s;
    }

    /* ─────────── duplicates across a carrier ─────────── */
    /**
     * elements — analyzeElement results (array). Mutates: a `latin` issue whose
     * run found its Russian duplicate becomes `latin_dup` (ok, or warn when the
     * measured style differs), and each pair is returned.
     * opts.manual — Map(srcIndex → dupIndex) set by the person.
     * opts.metrics — Array of {h, color, bg, caps} per element (photo mode).
     */
    function pairDuplicates(elements, opts) {
        opts = opts || {};
        const pairs = [];
        const manual = opts.manual || new Map();
        for (let i = 0; i < elements.length; i++) {
            const el = elements[i];
            for (const iss of el.issues) {
                if (iss.code !== 'latin') continue;
                const r = iss.run;
                let best = null;
                // 1) inline: the same element carries Russian words that translate the run
                const inlineRu = el.text.slice(0, r.start) + ' ' + el.text.slice(r.end);
                const covIn = CYR.test(inlineRu) ? coverage(r.gloss, inlineRu) : null;
                if (covIn != null && covIn >= 0.5) best = { dup: i, coverage: covIn, inline: true };
                // 2) manual pairing beats any guess
                if (manual.has(i)) {
                    const j = manual.get(i);
                    if (j == null || j < 0) { if (!best) continue; }          // the person says: no duplicate
                    else if (elements[j]) best = { dup: j, coverage: coverage(r.gloss, elements[j].text), manual: true };
                }
                // 3) another element translating it
                if (!best) {
                    for (let j = 0; j < elements.length; j++) {
                        if (j === i || !elements[j].hasCyr) continue;
                        const d = coverageDetail(r.gloss, elements[j].text);
                        if (!d || d.cov < 0.5) continue;
                        const score = d.cov * 2 + d.prec;
                        if (!best || score > best.score) best = { dup: j, coverage: d.cov, score };
                    }
                }
                if (!best) continue;
                const pair = { src: i, dup: best.dup, run: r, coverage: best.coverage, inline: !!best.inline, manual: !!best.manual };
                if (opts.metrics && !best.inline && opts.metrics[i] && opts.metrics[best.dup]) {
                    pair.style = compareMetrics(opts.metrics[i], opts.metrics[best.dup]);
                }
                iss.code = 'latin_dup';
                iss.pair = pair;
                iss.sev = pair.style && pair.style.sev ? pair.style.sev : (best.coverage < 0.75 && !best.manual ? 'warn' : 'ok');
                if (!pair.style && best.coverage < 0.75 && !best.manual) iss.partial = true;
                pairs.push(pair);
            }
            el.status = statusOf(el.issues);
        }
        return pairs;
    }

    /* ─────────── measured style (photo) ─────────── */
    function srgbToLin(c) { c /= 255; return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
    function luminance(rgb) { return 0.2126 * srgbToLin(rgb[0]) + 0.7152 * srgbToLin(rgb[1]) + 0.0722 * srgbToLin(rgb[2]); }
    function contrast(a, b) {
        const la = luminance(a), lb = luminance(b);
        return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
    }
    function toLab(rgb) {
        const r = srgbToLin(rgb[0]), g = srgbToLin(rgb[1]), b = srgbToLin(rgb[2]);
        let x = (r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047;
        let y = (r * 0.2126 + g * 0.7152 + b * 0.0722);
        let z = (r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883;
        const f = t => t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116;
        x = f(x); y = f(y); z = f(z);
        return [116 * y - 16, 500 * (x - y), 200 * (y - z)];
    }
    function deltaE(a, b) {
        const A = toLab(a), B = toLab(b);
        return Math.sqrt(Math.pow(A[0] - B[0], 2) + Math.pow(A[1] - B[1], 2) + Math.pow(A[2] - B[2], 2));
    }

    /**
     * a — the foreign line, b — its Russian duplicate: {h, color, bg, caps}.
     * Heights are glyph-box heights in image pixels; a line set in capitals and a
     * line in mixed case differ in box height at the same font size, so that
     * comparison is flagged approximate rather than silently corrected.
     */
    function compareMetrics(a, b) {
        const out = { approx: !!(a.caps !== b.caps) };
        if (a.h > 0 && b.h > 0) {
            out.ratio = b.h / a.h;
            out.size = out.ratio >= 0.9 && out.ratio <= 1.12 ? 'same' : out.ratio < 0.9 ? (out.ratio < 0.75 ? 'much_smaller' : 'smaller') : 'larger';
        }
        if (a.color && b.color) {
            out.dE = deltaE(a.color, b.color);
            if (a.bg && b.bg) {
                out.cA = contrast(a.color, a.bg);
                out.cB = contrast(b.color, b.bg);
            }
            const paler = out.cA && out.cB && out.cB < out.cA * 0.75;
            out.color = paler ? 'paler' : out.dE > 30 ? 'different' : 'same';
        }
        let sev = 'ok';
        if (out.size === 'smaller' || out.color === 'different') sev = 'warn';
        if (out.size === 'much_smaller' || out.color === 'paler') sev = out.approx && out.size === 'much_smaller' && out.color !== 'paler' ? 'warn' : 'fail';
        out.sev = sev;
        return out;
    }

    /** Text colour and background of a glyph box from raw RGBA (Otsu on luminance). */
    function measureBox(rgba, width, box) {
        const x0 = Math.max(0, Math.floor(box.x0)), x1 = Math.min(width, Math.ceil(box.x1));
        const height = Math.floor(rgba.length / 4 / width);
        const y0 = Math.max(0, Math.floor(box.y0)), y1 = Math.min(height, Math.ceil(box.y1));
        if (x1 - x0 < 2 || y1 - y0 < 2) return null;
        const hist = new Array(256).fill(0);
        const lum = [];
        const step = Math.max(1, Math.floor(((x1 - x0) * (y1 - y0)) / 60000));
        let n = 0;
        for (let y = y0; y < y1; y++) {
            for (let x = x0; x < x1; x += step) {
                const i = (y * width + x) * 4;
                const L = Math.round(0.299 * rgba[i] + 0.587 * rgba[i + 1] + 0.114 * rgba[i + 2]);
                hist[L]++; lum.push([L, i, (x === x0 || x >= x1 - step || y === y0 || y === y1 - 1)]); n++;
            }
        }
        // Otsu threshold
        let sum = 0; for (let t = 0; t < 256; t++) sum += t * hist[t];
        let sumB = 0, wB = 0, best = 0, thr = 127;
        for (let t = 0; t < 256; t++) {
            wB += hist[t]; if (!wB) continue;
            const wF = n - wB; if (!wF) break;
            sumB += t * hist[t];
            const mB = sumB / wB, mF = (sum - sumB) / wF;
            const between = wB * wF * (mB - mF) * (mB - mF);
            if (between > best) { best = between; thr = t; }
        }
        // The class that owns the surroundings is the background. Judged on a RING
        // just outside the box: the detector's box is tight, and on large bold
        // letters (BEAUTY STUDIO, БАРБЕРШОП) the strokes own the box's own edge —
        // the background was taken for the ink, both lines measured the colour of
        // the wall (#f8f2ed), and «paler» could never fire. The edge is the
        // fallback when the box touches the frame.
        const pad = Math.max(3, Math.round((y1 - y0) * 0.25));
        const rx0 = Math.max(0, x0 - pad), rx1 = Math.min(width, x1 + pad), ry0 = Math.max(0, y0 - pad), ry1 = Math.min(height, y1 + pad);
        let ringDark = 0, ringAll = 0;
        const rstep = Math.max(1, Math.floor(((rx1 - rx0) * (ry1 - ry0)) / 40000));
        for (let y = ry0; y < ry1; y++) {
            const inRows = y >= y0 && y < y1;
            for (let x = rx0; x < rx1; x += rstep) {
                if (inRows && x >= x0 && x < x1) continue;
                const i = (y * width + x) * 4;
                const L = Math.round(0.299 * rgba[i] + 0.587 * rgba[i + 1] + 0.114 * rgba[i + 2]);
                ringAll++; if (L <= thr) ringDark++;
            }
        }
        let borderDark = 0, borderAll = 0;
        for (const [L, , edge] of lum) if (edge) { borderAll++; if (L <= thr) borderDark++; }
        const bgDark = ringAll >= 16 ? ringDark / ringAll > 0.5 : (borderAll ? borderDark / borderAll > 0.5 : false);
        const acc = { t: [0, 0, 0, 0], b: [0, 0, 0, 0] };
        // ink rows: a row counts when ≥ 2 % of its samples are text — the
        // vertical extent of the letters, tighter than the detector's padded box
        const rowInk = new Map();
        for (const [L, i] of lum) {
            const isDark = L <= thr;
            const isText = isDark !== bgDark;
            const bucket = isText ? acc.t : acc.b;
            bucket[0] += rgba[i]; bucket[1] += rgba[i + 1]; bucket[2] += rgba[i + 2]; bucket[3]++;
            const y = Math.floor(i / 4 / width);
            const r = rowInk.get(y) || [0, 0];
            r[1]++; if (isText) r[0]++;
            rowInk.set(y, r);
        }
        if (!acc.t[3] || !acc.b[3]) return null;
        let top = -1, bottom = -1;
        for (let y = y0; y < y1; y++) {
            const r = rowInk.get(y);
            if (r && r[1] && r[0] / r[1] >= 0.02) { if (top < 0) top = y; bottom = y; }
        }
        const avg = a => [Math.round(a[0] / a[3]), Math.round(a[1] / a[3]), Math.round(a[2] / a[3])];
        return { color: avg(acc.t), bg: avg(acc.b), ink: acc.t[3] / (acc.t[3] + acc.b[3]),
            inkH: top >= 0 ? bottom - top + 1 : (y1 - y0) };
    }

    /* ─────────── whole-carrier summary ─────────── */
    function summarize(elements) {
        const count = { fail: 0, warn: 0, note: 0, ok: 0 };
        const codes = {};
        for (const el of elements) {
            count[el.status] = (count[el.status] || 0) + 1;
            for (const i of el.issues) codes[i.code] = (codes[i.code] || 0) + 1;
        }
        const status = count.fail ? 'fail' : count.warn ? 'warn' : 'ok';
        return { status, count, codes, elements: elements.length };
    }

    /* ─────────── site pages: language versions ─────────── */
    /**
     * page — {lang, alternates:[{lang, href}]}. A page in a foreign language is a
     * permitted foreign-language VERSION (п. 3 ст. 10.1) when the site links a
     * Russian alternate for it; without one the visitor has no Russian page.
     */
    function pageLanguageVerdict(page) {
        const lang = String(page.lang || '').toLowerCase().slice(0, 2);
        const alts = page.alternates || [];
        const ruAlt = alts.find(a => /^ru\b/i.test(a.lang || ''));
        if (!lang || lang === 'ru') return { code: 'page_ru', sev: 'ok', ruAlt: null, foreignAlts: alts.filter(a => !/^(ru|x-default)/i.test(a.lang || '')) };
        if (ruAlt) return { code: 'page_foreign_version', sev: 'ok', ruAlt };
        return { code: 'page_foreign_no_ru', sev: 'fail', ruAlt: null };
    }

    return {
        VERSION, OBJECTS, SEV,
        norm, tokenize, maskAddresses, scriptOf,
        compileLexicon, lookupWord, formMatches,
        compileGlossary, glossRun, glossWord,
        latinRuns, runKind, coverage, coverageDetail, cyrStems, stemLite,
        analyzeElement, pairDuplicates, statusOf, summarize,
        compareMetrics, measureBox, contrast, deltaE,
        pageLanguageVerdict,
    };
});
